import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { 
  FileBox, Activity, Database, Lock, Sparkles, Loader2 
} from "lucide-react";

const schema = z.object({
  displayName: z.string().trim().min(2, "Nome muito curto").max(100),
  email: z.string().trim().email("Email inválido").max(255),
  password: z
    .string()
    .min(8, "Mínimo 8 caracteres")
    .max(72)
    .regex(/[A-Z]/, "Inclua uma letra maiúscula")
    .regex(/[a-z]/, "Inclua uma letra minúscula")
    .regex(/[0-9]/, "Inclua um número")
    .regex(/[^A-Za-z0-9]/, "Inclua um caractere especial"),
  invite: z
    .string()
    .trim()
    .min(1, "Informe o código de convite")
    .transform((v) => v.toUpperCase()),
});

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: "", email: "", password: "", invite: "" });
  const [loading, setLoading] = useState(false);

  // Força Light Mode na Landing Page
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({ title: "Verifique os campos", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);

    // Pré-valida o convite (feedback rápido; o trigger valida e consome no servidor)
    const { data: ok, error: vErr } = await supabase.rpc("validate_invite", { _code: parsed.data.invite });
    if (vErr || !ok) {
      setLoading(false);
      toast({ title: "Convite inválido", description: "Código não encontrado, expirado ou esgotado.", variant: "destructive" });
      return;
    }

    const { data: signData, error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { display_name: parsed.data.displayName, invite_code: parsed.data.invite },
      },
    });
    setLoading(false);
    if (error) {
      const msg = /invite/i.test(error.message)
        ? "Código de convite inválido ou esgotado."
        : error.message;
      toast({ title: "Erro no cadastro", description: msg, variant: "destructive" });
      return;
    }
    
    if (signData.session) {
      toast({ title: "Conta criada e logada", description: "Bem-vindo ao DataGrid XML!" });
      navigate("/upload");
    } else {
      toast({ title: "Conta criada", description: "Verifique seu email para confirmar a conta antes do primeiro acesso." });
      navigate("/login");
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground font-sans p-4 relative overflow-hidden">
      
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] bg-primary/5 liquid-blob blur-[100px] opacity-60" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-primary/10 liquid-blob blur-[120px] opacity-40" style={{ animationDelay: "-5s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-blue-500/5 liquid-blob blur-[150px] opacity-20" style={{ animationDelay: "-10s" }} />
      </div>

      {/* Main Container */}
      <div className="relative w-full max-w-[400px] z-10 animate-in fade-in zoom-in-95 duration-700">
        <div className="absolute -inset-4 bg-primary/5 rounded-[3rem] blur-3xl opacity-50" />
        
        <div className="glass-card p-8 relative border-white/20 dark:border-white/5">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4">
              <div className="absolute -inset-2 bg-primary/20 rounded-full blur-xl animate-pulse" />
              <div className="size-14 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/40 relative z-10 rotate-3 hover:rotate-0 transition-transform duration-500">
                <FileBox className="size-7 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-black tracking-tighter text-foreground mb-1">Criar <span className="text-primary italic">conta</span></h1>
            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-black opacity-60">DataGrid XML Analytics</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1 group">
              <Label htmlFor="name" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">Nome Completo</Label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <Activity className="size-3.5" />
                </div>
                <Input id="name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required 
                  placeholder="Seu nome"
                  className="input-premium h-11 pl-11 bg-white/5" />
              </div>
            </div>

            <div className="space-y-1 group">
              <Label htmlFor="email" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">Endereço de Email</Label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <Database className="size-3.5" />
                </div>
                <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required 
                  placeholder="exemplo@empresa.com"
                  className="input-premium h-11 pl-11 bg-white/5" />
              </div>
            </div>

            <div className="space-y-1 group">
              <Label htmlFor="password" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">Senha de Acesso</Label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <Lock className="size-3.5" />
                </div>
                <Input id="password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required 
                  placeholder="••••••••"
                  className="input-premium h-11 pl-11 bg-white/5" />
              </div>
            </div>

            <div className="space-y-1 group">
              <Label htmlFor="invite" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">Código de Convite</Label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <Sparkles className="size-3.5" />
                </div>
                <Input
                  id="invite"
                  value={form.invite}
                  onChange={(e) => setForm({ ...form, invite: e.target.value.toUpperCase() })}
                  placeholder="DIGITE SEU CÓDIGO"
                  maxLength={16}
                  autoComplete="off"
                  required
                  className="input-premium h-11 pl-11 uppercase font-black tracking-widest"
                />
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full h-11 text-[10px] font-black tracking-[0.2em]" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-3 animate-spin" /> PROCESSANDO...
                </span>
              ) : "FINALIZAR CADASTRO"}
            </Button>
          </form>

          <div className="mt-6 text-center border-t border-foreground/5 pt-6">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest">
              Já possui credenciais? <Link to="/login" className="text-primary font-black hover:scale-105 transition-transform inline-block ml-1">Entrar</Link>
            </p>
          </div>
        </div>

        {/* Legal Footer */}
        <div className="mt-6 text-center">
          <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-[0.2em] opacity-40">
            Powered by DataGrid XML Engine • 2024
          </p>
        </div>
      </div>
    </div>
  );
}
